import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Observation, ObservationReferenceRange } from 'fhir/r4';

@Component({
  selector: 'app-observation-value-display',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="obs-display-container">
      @if (showValue) {
        <div class="obs-value">
          @if (displayValue && displayValue !== '-') {
             <span class="value-text value-with-unit">{{ displayValue }}</span>
          }
          
          @if (interpretationBadge) {
            <span class="badge" [ngClass]="badgeClass">
              @if(badgeClass === 'high') { <span class="arrow">&uarr;</span> }
              @if(badgeClass === 'low') { <span class="arrow">&darr;</span> }
              {{ interpretationBadge }}
            </span>
          }
</div>

          @if (componentsText) {
            <div class="components-text">
              <span class="components-bracket"> ({{ componentsText }})</span>
            </div>
          }
        
      } @else {
        <!-- Only show the badge without the value -->
        <div class="obs-value">
          @if (interpretationBadge) {
            <span class="badge" [ngClass]="badgeClass">
              @if(badgeClass === 'high') { <span class="arrow">&uarr;</span> }
              @if(badgeClass === 'low') { <span class="arrow">&darr;</span> }
              {{ interpretationBadge }}
            </span>
          }
          </div>
          @if (componentsText) {
            <div class="components-text">
             <span class="components-bracket"> ({{ componentsText }})</span>
          </div>
            }
        
      }

      @if (referenceRanges.length) {
        <div class="obs-ranges">
          @for (range of referenceRanges; track $index) {
            <div class="range-text">
              <span class="range-type">{{ range.type }}</span>
              <span>{{ range.text }}</span>
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .obs-display-container {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .badge{
      white-space: nowrap;
    }
    .obs-value {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .value-text {
      font-weight: 500;
      color: var(--mat-sys-on-surface);
    }
    .badge {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .badge.high {
      background: #ffebee;
      color: #c62828;
    }
    .badge.low {
      background: #e3f2fd;
      color: #1565c0;
    }
    .badge.abnormal {
      background: #fff3e0;
      color: #ef6c00;
    }
    .obs-ranges {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .range-text {
      font-size: 11px;
      color: light-dark(rgba(0, 0, 0, 0.662745098), #bbb) !important;
    }
    .components-bracket {
      font-size: 12px;
      color: light-dark(rgba(0, 0, 0, 0.662745098), #bbb) !important;
      margin-left: 6px;
    }
    .range-type {
      font-weight: 500;
      margin-right: 4px;
    }
  `]
})
export class ObservationValueDisplayComponent implements OnChanges {
  @Input() observation!: Observation | null | undefined;
  @Input() showValue: boolean = true;

  displayValue: string = '';
  interpretationBadge: string | null = null;
  badgeClass: 'high' | 'low' | 'abnormal' | null = null;
  componentsText: string = '';
  referenceRanges: { type: string; text: string }[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['observation']) {
      this.updateDisplay();
    }
  }

  private updateDisplay(): void {
    if (!this.observation) {
      this.displayValue = '-';
      this.interpretationBadge = null;
      this.badgeClass = null;
      this.componentsText = '';
      this.referenceRanges = [];
      return;
    }

    this.displayValue = this.extractValue(this.observation);
    this.extractInterpretation(this.observation);
    this.extractRanges(this.observation);
    this.extractComponents(this.observation);
  }

  private extractComponents(obs: Observation): void {
    this.componentsText = '';
    if (Array.isArray(obs.component) && obs.component.length > 0) {
      const compStrs = obs.component.map((c: any) => {
        const cName = c.code?.text || c.code?.coding?.[0]?.display || '';
        const cVal = this.extractValue(c as Observation);
        
        let text = '';
        if (cName && cVal && cVal !== '-') text = `${cName}: ${cVal}`;
        else text = (cVal !== '-') ? cVal : cName;

        let rangeStr = '';
        if (Array.isArray(c.referenceRange)) {
          const ranges = c.referenceRange.map((r: any) => {
            const bounds = [];
            if (r.low?.value != null) bounds.push(r.low.value);
            if (r.high?.value != null) bounds.push(r.high.value);
            return bounds.length ? `[${bounds.join(' - ')}]` : '';
          }).filter(Boolean);
          if (ranges.length) {
            rangeStr = ranges.join(', ');
          }
        }

        let interpStr = '';
        if (Array.isArray(c.interpretation) && c.interpretation.length > 0) {
          interpStr = c.interpretation[0].text || c.interpretation[0].coding?.[0]?.display || '';
        }

        let out = text;
        if (rangeStr) out += ` ${rangeStr}`;
        if (interpStr) out += ` - ${interpStr}`;
        return out;
      }).filter(Boolean);
      
      if (compStrs.length) {
        this.componentsText = compStrs.join(', ');
      }
    }
  }

  private extractValue(obs: Observation): string {
    if (obs.valueQuantity) {
      const v = obs.valueQuantity.value ?? '';
      const u = obs.valueQuantity.unit || obs.valueQuantity.code || '';
      return `${v} ${u}`.trim();
    }

    // Special handling for Blood Pressure that only has components
    const coding = obs.code?.coding || [];
    const isBp = coding.some(c => c.code === '85354-9' || c.code === '8480-6' || c.code === '8462-4');
    if (isBp && Array.isArray(obs.component)) {
       const sys = obs.component.find((c: any) => c.code?.coding?.some((k: any) => k.code === '8480-6'));
       const dia = obs.component.find((c: any) => c.code?.coding?.some((k: any) => k.code === '8462-4'));
       if (sys?.valueQuantity?.value != null && dia?.valueQuantity?.value != null) {
          const unit = (sys.valueQuantity.unit || sys.valueQuantity.code || 'mmHg').replace('mm[Hg]', 'mmHg');
          return `${sys.valueQuantity.value}/${dia.valueQuantity.value} ${unit}`.trim();
       }
    }

    if (obs.valueString) return obs.valueString;
    if (obs.valueCodeableConcept) return obs.valueCodeableConcept.text || obs.valueCodeableConcept.coding?.[0]?.display || '';
    if (obs.valueInteger != null) return obs.valueInteger.toString();
    if (obs.valueBoolean != null) return obs.valueBoolean ? 'Yes' : 'No';
    return '-';
  }

  private extractInterpretation(obs: Observation): void {
    this.interpretationBadge = null;
    this.badgeClass = null;

    if (!obs.interpretation?.length) return;

    const firstInterp = obs.interpretation[0];
    const code = firstInterp.coding?.[0]?.code?.toUpperCase();
    const display = firstInterp.coding?.[0]?.display || firstInterp.text || code;
  
    if (code === 'H' || code === 'HU' || code === 'HH') {
      this.interpretationBadge = display || null;
      this.badgeClass = 'high';
    } else if (code === 'L' || code === 'LU' || code === 'LL') {
      this.interpretationBadge = display || null;
      this.badgeClass = 'low';
    } else if (code === 'A' || code === 'AA' || code === 'ABNORMAL') {
      this.interpretationBadge = display || 'Abnormal';
      this.badgeClass = 'abnormal';
    } else if (display) {
      this.interpretationBadge = display;
      const lowerDisplay = display.toLowerCase();
      if (lowerDisplay.includes('high')) this.badgeClass = 'high';
      else if (lowerDisplay.includes('low')) this.badgeClass = 'low';
      else if (lowerDisplay.includes('abnormal')) this.badgeClass = 'abnormal';
      else this.badgeClass = 'abnormal'; // neutral fallback
    }
  }

  private extractRanges(obs: Observation): void {
    this.referenceRanges = [];
    if (!obs.referenceRange?.length) return;

    for (const range of obs.referenceRange) {
      const text = this.formatRange(range);
      if (text) {
        let typeStr = range.type?.text || range.type?.coding?.[0]?.display || 'Range';
        // Capitalize first letter
        typeStr = typeStr.charAt(0).toUpperCase() + typeStr.slice(1);
        this.referenceRanges.push({ type: typeStr + ':', text });
      }
    }
  }

  private formatRange(range: ObservationReferenceRange): string {
    const lowStr = range.low?.value != null ? `${range.low.value} ${range.low.unit || range.low.code || ''}`.trim() : null;
    const highStr = range.high?.value != null ? `${range.high.value} ${range.high.unit || range.high.code || ''}`.trim() : null;
    
    if (lowStr && highStr) return `${lowStr} - ${highStr}`;
    if (lowStr) return `>= ${lowStr}`;
    if (highStr) return `<= ${highStr}`;
    if (range.text) return range.text;
    return '';
  }
}
